import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';

import TableWidget from '@/components/TableWidget';
import { PAGE_SIZES } from '@/constants/common';
import useConfirm from '@/hooks/useConfirm';
import useStyles from '@/pages/Announcements/Announcement.styles';
import { draftAnnouncementsAtom } from '@/pages/Announcements/Announcements.store';
import type { Announcement } from '@/pages/Announcements/Announcements.types';
import { isLayoutMobileAtom } from '@/store/layout';
import { isSidebarExpandedAtom } from '@/store/sidebar';

import useAnnouncements from '../Announcement.hooks';
import { AnnouncementDialogType } from '../AnnouncementDialog/AnnouncementDialog.constants';
import useAnnouncementDialog from '../AnnouncementDialog/AnnouncementDialog.hooks';
import { createColumns } from '../Announcements.utils';
import { AnnouncementTestEmailDialog } from '../AnnouncementTestEmailDialog';

const DraftedAnnouncements = () => {
  const { classes } = useStyles();
  const [draftAnnouncements] = useAtom(draftAnnouncementsAtom);
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [isSidebarExpanded] = useAtom(isSidebarExpandedAtom);
  const [testEmailOpen, setTestEmailOpen] = useState(false);

  const { setType, setOpen } = useAnnouncementDialog();
  const {
    deleteAnnouncement,
    publishAnnouncement,
    selectedAnnouncement,
    setSelectedAnnouncement,
    userIsInternal
  } = useAnnouncements();

  const { ConfirmationModal: AnnouncementDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Draft Announcement?',
      message: 'Please confirm that you want to delete this draft.',
      button: 'Delete'
    });

  const {
    ConfirmationModal: AnnouncementPublishModal,
    confirm: confirmPublish
  } = useConfirm({
    title: 'Publish Draft Announcement?',
    message: 'Please confirm that you want to publish this draft.',
    button: 'Publish'
  });

  const {
    ConfirmationModal: AnnouncementUnpublishModal,
    confirm: confirmUnpublish
  } = useConfirm({
    title: 'Unpublish Draft Announcement?',
    message: 'Please confirm that you want to unpublish this draft.',
    button: 'Unpublish'
  });

  const width = useMemo(() => {
    // mobile
    if (isLayoutMobile) return '100%';
    // desktop
    if (isSidebarExpanded) return 'calc(100vw - 21.5rem)';
    return 'calc(100vw - 7rem)';
  }, [isLayoutMobile, isSidebarExpanded]);

  const handleDelete = async (id: string) => {
    const confirmed = await confirmDelete();
    if (confirmed) {
      deleteAnnouncement.mutate(id);
    }
  };

  const handleEdit = (
    row: Announcement | ((prev: Announcement) => Announcement)
  ) => {
    setType(AnnouncementDialogType.EDIT);
    setSelectedAnnouncement(row);
    setOpen(true);
  };

  const handlePublish = async (
    rowInfo: Announcement,
    revert: boolean = false
  ) => {
    // unpublish
    if (revert) {
      const confirmed = await confirmUnpublish();
      if (confirmed) {
        publishAnnouncement.mutate({
          announcement_id: rowInfo.id,
          send_email: rowInfo.send_email,
          scheduled_at: rowInfo.scheduled_at || null,
          revert
        });
      }
      return;
    }

    // publish
    const confirmed = await confirmPublish();
    if (confirmed) {
      publishAnnouncement.mutate({
        announcement_id: rowInfo.id,
        send_email: rowInfo.send_email,
        scheduled_at: rowInfo.scheduled_at || null,
        revert
      });
    }
  };

  const handleTestEmail = (rowInfo: Announcement) => {
    setSelectedAnnouncement(rowInfo);
    setTestEmailOpen(true);
  };

  const columns = useMemo(
    () =>
      createColumns(
        classes,
        userIsInternal,
        setOpen,
        setSelectedAnnouncement,
        setType,
        'draft',
        handleDelete,
        handleEdit,
        handlePublish,
        handleTestEmail
      ),
    [userIsInternal]
  );

  return (
    <div
      style={{
        height: '100%',
        width
      }}
    >
      <TableWidget
        title="Announcements"
        columns={columns}
        rows={draftAnnouncements}
        loading={false}
        header
        pageSizeOptions={PAGE_SIZES}
        disableRowSelectionOnClick
      />
      <AnnouncementDeleteModal />
      <AnnouncementPublishModal />
      <AnnouncementUnpublishModal />
      {testEmailOpen && (
        <AnnouncementTestEmailDialog
          open={testEmailOpen}
          announcement={selectedAnnouncement}
          onClose={() => setTestEmailOpen(false)}
        />
      )}
    </div>
  );
};

export default DraftedAnnouncements;
